import { Link, useLocation } from "react-router-dom";

import {
  SITE_BACK_TO_APP_LABEL,
  SITE_NAV_LINKS,
  SITE_WORDMARK,
} from "@/constants/site";

// 서브메뉴가 있는 항목("주관 / 서강대학교")은 서강대 관련 서브 페이지(3개 slug)
// 어디에 있어도 활성 상태로 보여야 하므로 대표 경로 접두사로 별도 판별한다.
const SOGANG_PATH_PREFIX = "/institutions/sogang-";

/**
 * 공개 사이트(랜딩/연구소개/기관/연구성과) 공용 상단 고정 헤더.
 * 좌측 워드마크 · 가운데 내비게이션 · 우측 대시보드 복귀 링크로 구성된다.
 * 모든 내비게이션 항목은 이제 내부 라우트로 연결되며, 하위 항목(children)이
 * 있는 항목("주관 / 서강대학교")은 hover/focus 시 서브메뉴를 보여준다.
 * CSS 전용(hover/focus-within) 드롭다운이라 JS 상태가 없으므로 `aria-expanded`는
 * 붙이지 않고, 대신 정적인 `aria-haspopup`/`role="menu"`/`role="menuitem"`으로
 * 서브메뉴가 있다는 사실만 스크린리더에 알린다.
 */
export default function SiteHeader() {
  const location = useLocation();

  // 서브메뉴가 있는 항목은 배열 위치가 아니라 `children` 유무로 판별한다
  // (SITE_NAV_LINKS 순서가 바뀌어도 활성 상태 계산이 조용히 깨지지 않도록).
  const isActive = (link: (typeof SITE_NAV_LINKS)[number]) =>
    link.children
      ? location.pathname.startsWith(SOGANG_PATH_PREFIX)
      : location.pathname.startsWith(link.to);

  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-mindcast-navy/95 text-white backdrop-blur">
      <div className="flex items-center justify-between px-6 py-4">
        <Link to="/" className="text-lg font-semibold">
          {SITE_WORDMARK}
        </Link>

        <nav
          aria-label="공개 사이트 내비게이션"
          className="hidden gap-6 text-sm text-white/80 md:flex"
        >
          {SITE_NAV_LINKS.map((link) =>
            link.children ? (
              <div key={link.label} className="group relative">
                <Link
                  to={link.to}
                  aria-haspopup="true"
                  className={
                    isActive(link) ? "text-mindcast-accent" : "hover:text-white"
                  }
                  data-slot="link"
                >
                  {link.label}
                </Link>
                <div
                  role="menu"
                  className="invisible absolute top-full left-0 z-50 pt-2 opacity-0 transition-opacity duration-150 group-focus-within:visible group-focus-within:opacity-100 group-hover:visible group-hover:opacity-100"
                >
                  <div className="min-w-max rounded-md bg-mindcast-navy py-2 shadow-lg">
                    {link.children.map((child) => (
                      <Link
                        key={child.label}
                        to={child.to}
                        role="menuitem"
                        className="block px-4 py-1.5 text-white/80 hover:text-white"
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <Link
                key={link.label}
                to={link.to}
                className={
                  isActive(link) ? "text-mindcast-accent" : "hover:text-white"
                }
              >
                {link.label}
              </Link>
            ),
          )}
        </nav>

        <Link to="/home" className="text-sm text-white/60 hover:text-white">
          {SITE_BACK_TO_APP_LABEL}
        </Link>
      </div>
    </header>
  );
}
